import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GameCanvas, type GameCanvasRef } from "./components/GameCanvas";
import { GameControls } from "./components/GameControls";
import { GameMenu } from "./components/GameMenu";
import { GameUI } from "./components/GameUI";
import { MiniMap } from "./components/MiniMap";
import { useGameState } from "./hooks/useGameState";
import { useGhostAI } from "./hooks/useGhostAI";
import { useMaze } from "./hooks/useMaze";
import { usePacmanMovement } from "./hooks/usePacmanMovement";
import { GAME_CONFIG } from "./types/GameConstants";
import {
    type Direction,
    type DifficultyPreset,
    type PacmanGameProps,
} from "./types/GameTypes";
import { GameRenderer } from "./rendering/GameRenderer";

const _KEY_TO_DIRECTION: Record<string, Direction> = {
    ArrowUp: "up",
    ArrowDown: "down",
    ArrowLeft: "left",
    ArrowRight: "right",
    w: "up",
    s: "down",
    a: "left",
    d: "right",
};

const _toCellKey = (x: number, y: number): string =>
    `${Math.floor(x / GAME_CONFIG.CELL_SIZE)},${Math.floor(y / GAME_CONFIG.CELL_SIZE)}`;

const _DIFFICULTY_PRESETS: DifficultyPreset[] = ["easy", "normal", "hard"];
const _DIFFICULTY_STORAGE_KEY = "pacman_difficulty_preset";

const _isDifficultyPreset = (value: string): value is DifficultyPreset =>
    _DIFFICULTY_PRESETS.includes(value as DifficultyPreset);

export function PacmanGame({
    width = GAME_CONFIG.DEFAULT_WIDTH,
    height = GAME_CONFIG.DEFAULT_HEIGHT,
    className = "",
}: PacmanGameProps) {
    const canvasRef = useRef<GameCanvasRef>(null);
    const [hitCooldownUntil, setHitCooldownUntil] = useState<number>(0);
    const [isMiniMapVisible, setIsMiniMapVisible] = useState<boolean>(true);
    const [difficultyPreset, setDifficultyPreset] =
        useState<DifficultyPreset>("normal");

    const {
        gameState,
        score,
        lives,
        level,
        setLives,
        setScore,
        startGame,
        pauseGame,
        addScore,
        loseLife,
        winGame,
    } = useGameState();

    const {
        maze,
        dots,
        powerPellets,
        initializeMaze,
        isValidPosition,
        removeDot,
        removePowerPellet,
    } = useMaze();

    const { pacman, pacmanMouth, handleInput, updatePacman, resetPacman } =
        usePacmanMovement(isValidPosition, width);

    const {
        ghosts,
        frightenedTimer,
        initializeGhosts,
        updateGhosts,
        activateFrightenedMode,
        eatGhost,
        setFrightenedTimer,
    } = useGhostAI(
        pacman,
        isValidPosition,
        width,
        height,
        score,
        difficultyPreset,
    );

    const canRenderBoard = useMemo(
        () => maze.length > 0 && maze[0]?.length > 0,
        [maze],
    );

    const resetMatch = useCallback(() => {
        initializeMaze();
        initializeGhosts();
        resetPacman();
        setFrightenedTimer(0);
        setScore(0);
        setLives(GAME_CONFIG.INITIAL_LIVES);
    }, [
        initializeMaze,
        initializeGhosts,
        resetPacman,
        setFrightenedTimer,
        setScore,
        setLives,
    ]);

    const handleStart = useCallback(() => {
        resetMatch();
        startGame();
    }, [resetMatch, startGame]);

    const handleTogglePause = useCallback(() => {
        if (gameState !== "playing" && gameState !== "paused") return;
        pauseGame();
    }, [gameState, pauseGame]);

    const handleRestart = useCallback(() => {
        handleStart();
    }, [handleStart]);

    useEffect(() => {
        initializeMaze();
        initializeGhosts();
    }, [initializeGhosts, initializeMaze]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const storedPreset = window.localStorage.getItem(_DIFFICULTY_STORAGE_KEY);
        if (storedPreset && _isDifficultyPreset(storedPreset)) {
            setDifficultyPreset(storedPreset);
        }
    }, []);

    useEffect(() => {
        if (typeof window === "undefined") return;
        window.localStorage.setItem(_DIFFICULTY_STORAGE_KEY, difficultyPreset);
    }, [difficultyPreset]);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            const key = event.key;
            const lowered = key.toLowerCase();
            const direction = _KEY_TO_DIRECTION[key] || _KEY_TO_DIRECTION[lowered];

            if (key === "1") {
                event.preventDefault();
                setDifficultyPreset("easy");
                return;
            }

            if (key === "2") {
                event.preventDefault();
                setDifficultyPreset("normal");
                return;
            }

            if (key === "3") {
                event.preventDefault();
                setDifficultyPreset("hard");
                return;
            }

            if (key === " " || key === "Spacebar") {
                event.preventDefault();
                if (
                    gameState === "menu" ||
                    gameState === "gameOver" ||
                    gameState === "win"
                ) {
                    handleStart();
                }
                return;
            }

            if (lowered === "p") {
                event.preventDefault();
                handleTogglePause();
                return;
            }

            if (key === "Escape") {
                event.preventDefault();
                handleTogglePause();
                return;
            }

            if (lowered === "m") {
                event.preventDefault();
                setIsMiniMapVisible((prev) => !prev);
                return;
            }

            if (!direction || gameState !== "playing") return;
            event.preventDefault();
            handleInput(direction);
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [gameState, handleInput, handleStart, handleTogglePause]);

    useEffect(() => {
        if (gameState !== "playing") return;

        const intervalId = window.setInterval(() => {
            updatePacman(1000 / GAME_CONFIG.FPS);
            updateGhosts(1000 / GAME_CONFIG.FPS);
        }, 1000 / GAME_CONFIG.FPS);

        return () => window.clearInterval(intervalId);
    }, [gameState, updateGhosts, updatePacman]);

    useEffect(() => {
        if (gameState !== "playing") return;
        const key = _toCellKey(pacman.x, pacman.y);

        if (dots.has(key)) {
            removeDot(key);
            addScore(GAME_CONFIG.DOT_SCORE);
        }

        if (powerPellets.has(key)) {
            removePowerPellet(key);
            addScore(GAME_CONFIG.POWER_PELLET_SCORE);
            activateFrightenedMode();
        }
    }, [
        activateFrightenedMode,
        addScore,
        dots,
        gameState,
        pacman.x,
        pacman.y,
        powerPellets,
        removeDot,
        removePowerPellet,
    ]);

    useEffect(() => {
        if (gameState !== "playing") return;
        if (dots.size > 0 || powerPellets.size > 0) return;
        winGame();
    }, [dots.size, gameState, powerPellets.size, winGame]);

    useEffect(() => {
        if (gameState !== "playing") return;
        if (Date.now() < hitCooldownUntil) return;

        for (let i = 0; i < ghosts.length; i += 1) {
            const ghost = ghosts[i];
            const dx = ghost.x - pacman.x;
            const dy = ghost.y - pacman.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance > GAME_CONFIG.CELL_SIZE * 0.75) continue;

            if (ghost.mode === "frightened") {
                eatGhost(i);
                addScore(GAME_CONFIG.GHOST_SCORE);
                continue;
            }

            if (ghost.mode !== "eaten") {
                loseLife();
                resetPacman();
                initializeGhosts();
                setHitCooldownUntil(Date.now() + 1200);
                break;
            }
        }
    }, [
        addScore,
        eatGhost,
        gameState,
        ghosts,
        hitCooldownUntil,
        initializeGhosts,
        loseLife,
        pacman.x,
        pacman.y,
        resetPacman,
    ]);

    useEffect(() => {
        const ctx = canvasRef.current?.getContext();
        if (!ctx || !canRenderBoard) return;

        GameRenderer.render(
            ctx,
            maze,
            dots,
            powerPellets,
            pacman,
            pacmanMouth,
            ghosts,
            frightenedTimer,
            score,
            lives,
            level,
            width,
            height,
        );
    }, [
        canRenderBoard,
        dots,
        frightenedTimer,
        ghosts,
        height,
        level,
        lives,
        maze,
        pacman,
        pacmanMouth,
        powerPellets,
        score,
        width,
    ]);

    return (
        <div className={`relative mx-auto w-full max-w-[760px] ${className}`}>
            <GameCanvas
                ref={canvasRef}
                width={width}
                height={height}
                className="h-auto w-full"
                onClick={gameState !== "playing" ? handleStart : undefined}
            />
            <GameMenu
                gameState={gameState}
                score={score}
                onStart={handleStart}
                onTogglePause={handleTogglePause}
                difficultyPreset={difficultyPreset}
            />
            <MiniMap
                maze={maze}
                pacman={pacman}
                ghosts={ghosts}
                visible={isMiniMapVisible}
            />
            <GameUI
                score={score}
                lives={lives}
                level={level}
                frightenedTimer={frightenedTimer}
                gameState={gameState}
                width={width}
            />
            <GameControls
                gameState={gameState}
                isMiniMapVisible={isMiniMapVisible}
                difficultyPreset={difficultyPreset}
                onTogglePause={handleTogglePause}
                onRestart={handleRestart}
                onChangeDifficulty={setDifficultyPreset}
                onToggleMiniMap={() =>
                    setIsMiniMapVisible((current) => !current)
                }
            />
        </div>
    );
}
